<template>
  <FreeSearchFormCalendar
    :name="name"
    :title="$t('main.dates.title')"
    :selected-dates="props.selectedDates"
    :mode="calendar.getMode()"
    :min-date="props.minDate"
    :max-date="props.maxDate"
    :marked-dates="props.markedDates"
    :prices="getPrices"
    is-new-calendar-version
    :is-filtered="!props.multiTripMode && !!selectedAirline"
    @onDatesCommit="emit('onDatesSelected', $event)"
    @onOpen="onOpen"
    @onClose="onClose"
    @onModeChange="calendar.setMode"
    @onCalendarModeChange="onCalendarModeChange"
    @onCalendarIntersect="onCalendarIntersect"
  >
    <template #header="{ dates, isActiveMode, clearSecondDate }">
      <div>
        <!--
          Multi-trip legs only ever pick a single date, so the dual
          “Туда / Обратно” pickers are hidden in that mode.
        -->
        <div
          v-if="!props.multiTripMode"
          class="grid h-14 grid-flow-col grid-cols-2 gap-2"
        >
          <CalendarDateInput
            :placeholder="$t('main.dates.placeholder.departure.short')"
            :date="dates[0]"
            :is-active="
              isActiveMode(searchFormCalendar.config.CALENDAR_MODES.SINGLE)
            "
            @click="
              calendar.setMode(searchFormCalendar.config.CALENDAR_MODES.SINGLE)
            "
          />
          <CalendarDateInput
            :placeholder="$t('main.dates.placeholder.arrival.short')"
            :date="dates[1]"
            :is-active="
              isActiveMode(searchFormCalendar.config.CALENDAR_MODES.RANGE)
            "
            clearable
            @onDateClear="clearSecondDate"
            @click="
              calendar.setMode(searchFormCalendar.config.CALENDAR_MODES.RANGE)
            "
          />
        </div>
        <FullScreenLoader v-if="isFilterChangeLoader">
          <CalendarLoader :airline="selectedAirline" />
        </FullScreenLoader>
        <CalendarFilter
          v-if="!props.multiTripMode && calendar.getAirlines()?.length"
          :airlines="calendar.getAirlines()"
          :is-direct-flights="calendar.isDirectFligths.value"
          :selected-airline="selectedAirline"
          @on-filter-by-airline="onFilterByAirline"
          @onAirlinesListBottomSheet="bottomSheet.show(calendarBottomSheet)"
        />
        <FilterAirlinesBottomSheet
          v-if="!props.multiTripMode"
          :name="calendarBottomSheet"
          :airlines="calendar.getAirlines()"
          :is-direct-flights="calendar.isDirectFligths.value"
          :selected-airline="selectedAirline"
          @on-airline-click="onAirlineClick($event)"
          @on-reset-filter="onResetFilter()"
        />
      </div>
    </template>
    <template #button-content="{ dates }">
      <div v-if="isDatePricesExist(dates)">
        <i18n-t
          class="text-lg font-semibold text-white"
          tag="p"
          keypath="main.searchFormCalendar.selectedDateText"
        >
          <template #price>
            <span v-price="selectedDatePriceText(dates)" />
          </template>
        </i18n-t>
        <p class="text-2xs font-medium text-white">
          {{ calendarLib.formatSelectedDates(dates, isDatePricesExist(dates)) }}
        </p>
      </div>
      <p v-else class="text-lg font-semibold text-white">
        {{ t('general.select') }}
        {{ calendarLib.formatSelectedDates(dates, isDatePricesExist(dates)) }}
      </p>
    </template>
  </FreeSearchFormCalendar>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { FreeSearchFormCalendar, FullScreenLoader } from 'freedom-shared';
  import { searchFormCalendar, controllerModel, dateUtils } from 'shared';
  import type { CabinClass, PassengerType } from '@aviata/platform-client';
  import type { Dict, LowPrices } from '@aviata/platform-types';

  import { calendarBase, calendarLib } from '@/features/calendar';

  import { api } from '@/shared/api';

  import CalendarDateInput from './CalendarDateInput.vue';
  import CalendarFilter from './CalendarFilter.vue';
  import FilterAirlinesBottomSheet from './FilterAirlinesBottomSheet.vue';
  import CalendarLoader from './CalendarLoader.vue';

  type Passengers = Record<PassengerType, number>;

  const props = withDefaults(
    defineProps<{
      dep?: string;
      arr?: string;
      name: string;
      isYouth: boolean;
      passengers: Passengers;
      cabinClass: CabinClass;
      selectedDates: string[];
      /**
       * Multi-trip leg picker mode: single-date only, dual header inputs are
       * hidden and `range` mode toggling is suppressed.
       */
      multiTripMode?: boolean;
      /** Earliest selectable date — typically the previous leg's date. */
      minDate?: string;
      /** Latest selectable date — typically the next leg's date. */
      maxDate?: string;
      /**
       * Dates already selected on *other* legs of a multi-trip search; rendered
       * with a green outline so the user can see them in context.
       */
      markedDates?: string[];
    }>(),
    {
      dep: undefined,
      arr: undefined,
      multiTripMode: false,
      minDate: undefined,
      maxDate: undefined,
      markedDates: () => [],
    },
  );

  const emit = defineEmits<{
    onDatesSelected: [dates: string[]];
    onSelectAirline: [airline: Dict.Airlines.Airline | null];
  }>();

  const { t } = useI18n();

  const calendarBottomSheet = 'search-calendar-bottom-sheet';
  const filterLoaderDelay = 400;
  const lastMonthIndex = 11;

  const isFilterChangeLoader = ref(false);

  const calendar = calendarBase.model.useCalendar({
    fetchLowPrices: (params: LowPrices.V2.ExtendedSearchCalendarParams) => {
      return api.lowPrices.getSearchCalendarV2(params).then(res => res.data);
    },
  });

  const bottomSheet = controllerModel.useBottomSheet();
  const selectedAirline = ref<Dict.Airlines.Airline | null>(null);
  const depToArrResponse = computed(
    () => calendar.searchCalendarMap.value.depToArrResponse,
  );
  const arrToDepResponse = computed(
    () => calendar.searchCalendarMap.value.arrToDepResponse,
  );

  watch([() => props.arr, () => props.dep], () => {
    calendar.clearAirlines();
  });

  const getLowPrices = async () => {
    const { dep, arr, isYouth, passengers, cabinClass } = props;

    const dateFrom = dateUtils.getTomorrowDate();
    const dateTo = dateUtils.getFutureDate(calendar.getVisibleItems() * 30);

    const params = {
      arr,
      dep,
      isYouth,
      cabinClass,
      passengers,
      dateFrom,
      dateTo,
    };

    if (arr && dep) {
      const directFlightPricesPromise = calendar.getLowPrices(params);
      const returnFlightPricesPromise = calendar.getLowPrices({
        ...params,
        dep: arr,
        arr: dep,
        arrToDep: true,
      });
      await Promise.all([directFlightPricesPromise, returnFlightPricesPromise]);
    }
  };

  const onCalendarIntersect = (items: number) => {
    if (items === lastMonthIndex) {
      calendar.setVisibleItems(lastMonthIndex);
    } else {
      calendar.setVisibleItems(items + 2);
    }
    getLowPrices();
  };

  const getDepPrices = computed(() => {
    if (!depToArrResponse.value) {
      return;
    }

    return calendarLib.getNewPricesArr(
      depToArrResponse.value,
      selectedAirline.value,
    );
  });

  const getArrPrices = computed(() => {
    if (!arrToDepResponse.value) {
      return;
    }
    return calendarLib.getNewPricesArr(
      arrToDepResponse.value,
      selectedAirline.value,
    );
  });

  const getPrices = computed(() => {
    if (calendar.getMode() === 'single' && calendar) {
      return getDepPrices.value;
    }
    return getArrPrices.value;
  });

  const onFilterByAirline = (airline: Dict.Airlines.Airline) => {
    bottomSheet.hide(calendarBottomSheet);
    if (selectedAirline.value && selectedAirline.value.iata === airline.iata) {
      selectedAirline.value = null;
    } else {
      selectedAirline.value = airline;
    }
    emit('onSelectAirline', selectedAirline.value);
    isFilterChangeLoader.value = true;
    setTimeout(() => {
      isFilterChangeLoader.value = false;
    }, filterLoaderDelay);
  };

  const onOpen = async () => {
    const params = bottomSheet.getParams(props.name);
    const requestedMode =
      (params?.mode as searchFormCalendar.config.CalendarMode) ??
      searchFormCalendar.config.CALENDAR_MODES.SINGLE;
    const mode = props.multiTripMode
      ? searchFormCalendar.config.CALENDAR_MODES.SINGLE
      : requestedMode;
    calendar.setMode(mode);
    await getLowPrices();
    calendar.intersectAirlines();
  };

  const onClose = () => {
    calendar.clearSearchCalendarMap();
    selectedAirline.value = null;
  };

  const onCalendarModeChange = (shouldKeepRangeMode: boolean) => {
    // Multi-trip legs are single-date only — keep the picker pinned to single
    // even if the underlying calendar tries to switch to range.
    if (props.multiTripMode) {
      calendar.setMode('single');
      return;
    }
    if (calendar.getMode() === 'single' || shouldKeepRangeMode) {
      calendar.setMode('range');
    } else {
      calendar.setMode('single');
    }
  };

  const onResetFilter = () => {
    selectedAirline.value = null;
    bottomSheet.hide(calendarBottomSheet);
  };

  const onAirlineClick = ({
    airline,
    idx,
  }: {
    idx: number;
    airline: Dict.Airlines.Airline;
  }) => {
    onFilterByAirline(airline);
    calendar.moveAirlineToTop(idx);
  };

  const selectedDatePriceText = (dates: string[]) => {
    if (getDepPrices.value && getArrPrices.value) {
      return (
        Number(getDepPrices.value[dates[0]]?.price ?? 0) +
        Number(getArrPrices.value[dates[1]]?.price ?? 0)
      );
    }
    return null;
  };

  const isDatePricesExist = (dates: string[]) => {
    if (!getDepPrices.value || !getArrPrices.value) {
      return false;
    }
    if (dates.length === 1) {
      return Boolean(Number(getDepPrices.value[dates[0]]?.price));
    }

    if (dates.length === 2) {
      return (
        Boolean(Number(getDepPrices.value[dates[0]]?.price)) &&
        Boolean(Number(getArrPrices.value[dates[1]]?.price))
      );
    }

    return false;
  };
</script>
